import { useMemo, memo } from "react";
import toast from "react-hot-toast";
import { FormControl, Select, MenuItem } from "@mui/material";
import { supabase } from "../../../../../supabase";
import { useMainContext } from "../../../../../context/useMainContext";
import { red, green, purple, blue } from "@mui/material/colors";

export const statusOptions = [
  { value: "Pending", label: "To check", aliases: ['To Check', 'to-check'], color: "action.selected" },
  { value: "Contacted", label: "Contacted", color: blue[50] },
  { value: "No Mail", label: "No mail", color: "action.selected" },
  { value: "Other", label: "Other", color: "action.selected" },
  { value: "Approved Paid", label: "Approved booking", color: green[50] },
  { value: "Approved Barter", label: "Approved barter", color: green[50] },
  { value: "Disapproved/Data", label: "Disapproved data", aliases: ['Disapproved data'], color: red[50] },
  { value: "Disapproved/Manual Check", label: "Disapproved manual", color: red[50] },
  { value: "Blacklist", label: "Disapproved blacklist", color: red[50] },
  { value: "Wishlist", label: "Wishlist", color: purple[50] },
  { value: "Already had coop", label: "Already had co-op", color: blue[50] },
];

const StatusCell = ({ row, col }) => {
  const { user, setRows, isGuest, saveUndoData, setLoadingData } = useMainContext();

  const status = useMemo(
    () => statusOptions?.find((el) => el.value === row[col] || el.aliases?.includes(row[col])),
    [row[col]]
  );

  const handleStatusChange = async (e) => {
    const oldStatus = row.status;
    const newStatus = e.target.value;
    const updateRowInState = (updatesOrFn) => {
      setRows((prevRows) =>
        prevRows.map((r) => {
          if (r.id !== row.id) return r;
          const updates = typeof updatesOrFn === "function" ? updatesOrFn(r) : updatesOrFn;
          return { ...r, ...updates };
        })
      );
    };

    const saveStatusUpdate = async (status, historyEntry = null, removeHistoryId = null) => {
      try {
        setLoadingData(true);
        const { error: statusError } = await supabase.from("influencers").update({ status }).eq("id", row.id);
        if (statusError) throw statusError;
        // Remove from history if provided
        if (removeHistoryId) {
          await supabase.from("status_history").delete().eq("id", removeHistoryId);
        }
        if (historyEntry) {
          const { data: newHistoryEntry, error: historyError } = await supabase
            .from("status_history")
            .insert(historyEntry)
            .select()
            .single();
          if (historyError) {
            console.error("Failed to save status history:", historyError);
            updateRowInState({ status }); // Update UI even if history fails
            return null;
          }
          updateRowInState((r) => ({
            status,
            status_history: [newHistoryEntry, ...(r.status_history || [])],
          }));
          return newHistoryEntry;
        }
        // Update UI with status and handle history removal
        updateRowInState((r) => ({
          status,
          status_history: removeHistoryId 
            ? (r.status_history || []).filter((h) => h.id !== removeHistoryId)
            : r.status_history || [],
        }));
        return null;
      } finally {
        setLoadingData(false);
      }
    };

    try {
      const historyEntry = {
        influencer_id: row.influencer_id || row.id,
        assigned_member: user?.name || "Unknown",
        status: newStatus,
        previous_status: oldStatus,
      };
      const newHistoryEntry = await saveStatusUpdate(newStatus, historyEntry);
      saveUndoData({
        action: "Status changed",
        undoFunction: () => saveStatusUpdate(oldStatus, null, newHistoryEntry?.id),
        redoFunction: () => saveStatusUpdate(newStatus, historyEntry),
      });
      toast.success("Status updated");
    } catch (error) {
      console.error("Status update failed:", error);
      toast.error("Failed to update status");
    }
  };

  return (
    <FormControl size="small" variant="outlined">
      <Select
        value={status?.value || "Pending"}
        onChange={handleStatusChange}
        displayEmpty
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          bgcolor: status?.color || "action.selected",
        }}
        disabled={isGuest}
      >
        {statusOptions.map((status) => (
          <MenuItem key={status.value} value={status.value}>
            {status.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default memo(StatusCell);
